import * as React from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import type { fetchConnectFragmentsAction } from 'state/actions/connect-addon';
import type { WebFragment } from 'state/persisted/connect-addon';
import type { ConnectFragmentLocation } from 'state/selectors/connect-addon';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next/withAnalyticsEvents';
import { token } from '@atlaskit/tokens';
import { OPERATIONAL_EVENT_TYPE } from '@atlassian/analytics-web-react';
import {
	ConnectRequestCreateEventType,
	sendConnectTimeoutEventForRequestCreation,
} from '@atlassian/help-center-common-util/analytics/events';
import { getUniqueAppKeysFromConnectFragments } from '@atlassian/help-center-common-util/connect';
import type { ConnectFieldResult } from './connect-field';
import { ConnectField } from './connect-field';
import type { ConnectFieldsApi, ValidationResult } from './connect-fields-provider';

import type { ErrorResponse } from 'epics/model/types';

export interface Props {
	className?: string;
	portalId: number | undefined;
	requestTypeId: number | undefined;
	location: ConnectFragmentLocation;
}

export interface StateProps {
	connectFragments: WebFragment[] | { error: ErrorResponse } | undefined;
}

export interface DispatchProps {
	fetchConnectFragments: typeof fetchConnectFragmentsAction;
}

export interface ContextProps {
	onConnectFieldMount: (api: ConnectFieldsApi) => void;
}

class ConnectFields extends React.PureComponent<
	Props & StateProps & DispatchProps & ContextProps & WithAnalyticsEventsProps
> {
	private connectFieldRefs: ConnectField[] = [];

	componentDidMount() {
		const { fetchConnectFragments, connectFragments, requestTypeId, portalId } = this.props;
		if (!connectFragments) {
			fetchConnectFragments({ portalId, requestTypeId, portalPage: 'CREATE_REQUEST' });
			this.fireConnectFragmentsDataFetchAnalytics();
		} else {
			this.fireConnectFragmentAnalytics();
		}

		this.props.onConnectFieldMount(this);
	}

	componentDidUpdate(prevProps: Props & StateProps) {
		const { fetchConnectFragments, connectFragments, requestTypeId, portalId } = this.props;
		const requestTypeUpdated =
			prevProps.requestTypeId !== requestTypeId || prevProps.portalId !== portalId;

		if (requestTypeUpdated) {
			fetchConnectFragments({ portalId, requestTypeId, portalPage: 'CREATE_REQUEST' });
		}

		if (!prevProps.connectFragments && connectFragments) {
			this.fireConnectFragmentAnalytics();
		} else if (prevProps.connectFragments && connectFragments) {
			const previouslyErrored = 'error' in prevProps.connectFragments;
			const currentlyErrored = 'error' in connectFragments;
			if (previouslyErrored !== currentlyErrored) {
				this.fireConnectFragmentAnalytics();
			}
		}
	}

	fireConnectFragmentsDataFetchAnalytics = () => {
		const { createAnalyticsEvent, location } = this.props;

		if (createAnalyticsEvent) {
			const analyticsEvent = createAnalyticsEvent({
				location,
				analyticsType: OPERATIONAL_EVENT_TYPE,
				action: 'data_fetched',
			});

			// Hack to override the componentName set in ./index.tsx. We do this to offer consistent naming across all Connect fragment operational events
			analyticsEvent.context.push({ componentName: 'ConnectFragment' });

			analyticsEvent.fire();
		}
	};

	fireConnectFragmentAnalytics = () => {
		const { createAnalyticsEvent, connectFragments, location, portalId, requestTypeId } =
			this.props;

		if (!createAnalyticsEvent || !connectFragments) return;

		const isError = 'error' in connectFragments;
		if (!isError && (connectFragments as WebFragment[]).length === 0) return;

		const uniqueAppKeys = !isError
			? getUniqueAppKeysFromConnectFragments(connectFragments as WebFragment[])
			: [];
		const analyticsEvent = createAnalyticsEvent({
			portalId,
			requestTypeId,
			location,
			analyticsType: OPERATIONAL_EVENT_TYPE,
			action: isError ? 'failed' : 'loaded',
			appKeys: uniqueAppKeys,
		});

		// Hack to override the componentName set in ./index.tsx. We do this to offer consistent naming across all Connect fragment operational events
		analyticsEvent.context.push({ componentName: 'ConnectFragment' });

		analyticsEvent.fire();
	};

	onRef = (el: ConnectField | null) => {
		el && this.connectFieldRefs.push(el);
	};

	validate = (requestTypeId?: number): Promise<ValidationResult[]> => {
		return Promise.all(this.connectFieldRefs.map((ref) => ref.validate())).then(
			(validateResults: ConnectFieldResult[]) => {
				this.sendConnectTimeoutEventForTimedOutResults(
					ConnectRequestCreateEventType.VALIDATE,
					validateResults,
					requestTypeId,
				);

				return validateResults.map((result: ConnectFieldResult) => {
					return {
						connectKey: result.connectKey,
						valid: Boolean(result.data),
						timedOut: result.timedOut,
					};
				});
			},
		);
	};

	serialize = (validatedConnectFields: string[], requestTypeId?: number) => {
		const serializeValidatedRefs = this.connectFieldRefs
			.filter((ref) => validatedConnectFields.includes(ref.props.connectKey))
			.map((ref) => ref.serialize());

		return Promise.all(serializeValidatedRefs).then((allData: ConnectFieldResult[]) => {
			this.sendConnectTimeoutEventForTimedOutResults(
				ConnectRequestCreateEventType.SERIALIZE,
				allData,
				requestTypeId,
			);

			// This used to return stringified serialized data, but now we are returning the data in array form
			// which is then used in src/view/request-create-form/request-create-form.tsx to and allows us to
			// add additional data from Forge apps.
			return allData.filter((result) => result && !result.timedOut).map((result) => result.data);
		});
	};

	render() {
		const { connectFragments, className, location } = this.props;
		if (!!connectFragments && !('error' in connectFragments) && connectFragments.length) {
			return (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
				<Root className={className}>
					{connectFragments.map((fragment: WebFragment) => (
						<ConnectField
							ref={this.onRef}
							key={fragment.key}
							connectKey={fragment.key}
							fragmentHtml={fragment.html}
							location={location}
						/>
					))}
				</Root>
			);
		}

		return null;
	}

	private sendConnectTimeoutEventForTimedOutResults = (
		eventType: ConnectRequestCreateEventType,
		results: ConnectFieldResult[],
		requestTypeId?: number,
	): void => {
		results.forEach(
			(result: ConnectFieldResult) =>
				result.timedOut &&
				sendConnectTimeoutEventForRequestCreation(eventType, result.connectKey, requestTypeId),
		);
	};
}

export default ConnectFields;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Root = styled.div`
	margin-top: ${token('space.400', '32px')};
`;
